import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Droplets, Calendar, Users, ClipboardCheck, FileText, Package, Check, ArrowRight, Briefcase, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Landing = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const features = [
    { icon: Users, title: 'Gestión de Equipo', description: 'Administra trabajadores, asigna tareas y controla su rendimiento.' },
    { icon: Calendar, title: 'Calendario Inteligente', description: 'Programa visitas recurrentes y gestiona urgencias.' },
    { icon: ClipboardCheck, title: 'Partes de Trabajo', description: 'Formularios digitales con checklist, parámetros y fotos.' },
    { icon: FileText, title: 'Reportes en PDF', description: 'Genera informes profesionales automáticamente.' },
    { icon: Package, title: 'Control de Material', description: 'Gestiona el inventario y registra el material usado.' },
    { icon: Droplets, title: 'CRM Especializado', description: 'Base de datos de clientes con historial completo.' }
  ];

  const plans = [
    {
      id: 'autonomo',
      name: 'Autónomo',
      monthlyPrice: 29,
      annualPrice: 290,
      description: 'Para profesionales independientes',
      icon: Briefcase,
      features: [
        '1 usuario (tú eres jefe y trabajador)',
        'Hasta 50 clientes',
        'Partes de trabajo ilimitados',
        'Plantillas de reportes',
        'Generación de PDF',
        'Asistente IA',
        'Soporte por email'
      ]
    },
    {
      id: 'pyme',
      name: 'PYME',
      monthlyPrice: 39,
      annualPrice: 390,
      description: 'Para pequeñas y medianas empresas',
      icon: Users,
      popular: true,
      features: [
        'Hasta 4 usuarios',
        'Hasta 150 clientes',
        'Partes de trabajo ilimitados',
        'Plantillas de reportes',
        'Generación de PDF',
        'Asistente IA',
        'Rol de administrador',
        'Soporte prioritario'
      ]
    },
    {
      id: 'empresa',
      name: 'Empresa',
      monthlyPrice: 49,
      annualPrice: 490,
      description: 'Para grandes empresas',
      icon: Building,
      features: [
        'Usuarios ilimitados',
        'Clientes ilimitados',
        'Partes de trabajo ilimitados',
        'Plantillas de reportes',
        'Generación de PDF',
        'Asistente IA',
        'Rol de administrador',
        'Soporte premium 24/7',
        'Informes avanzados'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Aqua Nexia | Software de Gestión para Empresas de Mantenimiento de Piscinas</title>
        <meta name="description" content="Aqua Nexia es el software SaaS líder para empresas de mantenimiento de piscinas. Gestiona clientes, trabajadores, trabajos, partes de trabajo digitales y genera informes PDF automáticos. Prueba gratis 14 días." />
        <link rel="canonical" href="https://aquanexia.com/" />
      </Helmet>
      
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-sky-600 flex items-center justify-center">
                <Droplets className="w-6 h-6 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-slate-900">Aqua Nexia</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="login-btn">Iniciar sesión</Button>
              </Link>
              <Link to="/register">
                <Button data-testid="register-btn">Empezar gratis</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 to-white" />
        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight">
              Gestiona tu empresa de piscinas como un <span className="text-sky-600">profesional</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed">
              La plataforma todo-en-uno para empresas de mantenimiento de piscinas.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="h-12 px-8 text-base" data-testid="hero-cta-btn">
                  Prueba 14 días gratis <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" size="lg" className="h-12 px-8 text-base">Ya tengo cuenta</Button>
              </Link>
            </div>
            <p className="mt-4 text-sm text-slate-500">Cancela cuando quieras</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">
              Todo lo que necesitas para gestionar tu negocio
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.title} className="h-full hover:shadow-lg transition-shadow border-0 bg-white">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <CardTitle className="font-display text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-slate-600">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8" id="pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Elige tu plan</h2>
            <p className="text-slate-600 mt-4">14 días de prueba gratis. Cancela cuando quieras.</p>
            
            {/* Billing toggle */}
            <div className="mt-8 inline-flex items-center bg-slate-100 rounded-full p-1" data-testid="billing-toggle">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="billing-toggle-monthly"
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'annual'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid="billing-toggle-annual"
              >
                Anual
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                  -2 meses
                </span>
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const Icon = plan.icon;
              const displayPrice = billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice;
              const monthlyEquivalent = billingPeriod === 'annual' ? (plan.annualPrice / 12).toFixed(2) : null;
              const savings = billingPeriod === 'annual' ? (plan.monthlyPrice * 12) - plan.annualPrice : 0;
              return (
                <Card 
                  key={plan.id}
                  className={`relative h-full ${plan.popular ? 'border-2 border-sky-500 shadow-xl' : 'border'}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-sky-500 text-white px-4 py-1">Más popular</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center pb-2 pt-8">
                    <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center ${plan.popular ? 'bg-sky-100' : 'bg-slate-100'}`}>
                      <Icon className={`w-7 h-7 ${plan.popular ? 'text-sky-600' : 'text-slate-600'}`} />
                    </div>
                    <CardTitle className="font-display text-xl mt-4">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-slate-900">{displayPrice}€</span>
                      <span className="text-slate-500">/{billingPeriod === 'annual' ? 'año' : 'mes'}</span>
                      {billingPeriod === 'annual' && (
                        <div className="mt-1">
                          <span className="text-sm text-slate-500">{monthlyEquivalent}€/mes</span>
                          <span className="text-xs text-green-600 font-medium ml-2">Ahorras {savings}€/año</span>
                        </div>
                      )}
                    </div>
                    <ul className="space-y-3 mb-6 text-left">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to="/register" className="block">
                      <Button 
                        className={`w-full ${plan.popular ? 'bg-sky-600 hover:bg-sky-700' : ''}`}
                        variant={plan.popular ? 'default' : 'outline'}
                        data-testid={`pricing-btn-${plan.id}`}
                      >
                        Empezar prueba gratis
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-center text-sm text-slate-500 mt-8">
            Todos los planes incluyen 14 días de prueba gratis. El pago se procesa de forma segura con Stripe.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-sky-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">¿Listo para optimizar tu negocio?</h2>
          <Link to="/register">
            <Button size="lg" variant="secondary" className="mt-8 h-12 px-8 text-base bg-white text-sky-600 hover:bg-sky-50" data-testid="cta-bottom-btn">
              Comenzar prueba gratuita <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center">
                <Droplets className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-white">Aqua Nexia</span>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <Link to="/aviso-legal" className="text-slate-400 hover:text-white transition-colors">Aviso Legal</Link>
              <Link to="/politica-privacidad" className="text-slate-400 hover:text-white transition-colors">Privacidad</Link>
              <Link to="/terminos-condiciones" className="text-slate-400 hover:text-white transition-colors">Términos y Condiciones</Link>
              <Link to="/politica-cookies" className="text-slate-400 hover:text-white transition-colors">Cookies</Link>
            </nav>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-800 text-center">
            <p className="text-slate-400 text-sm">&copy; {new Date().getFullYear()} Aqua Nexia. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
